import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/constants/config";
import { scrollToSection } from "@/lib/scroll";
import { useLang } from "@/constants/i18n";

export const Hero = () => {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const portraitY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="grain relative overflow-hidden bg-ivory pt-[72px]"
        >
            <motion.div
                style={{ y: bgY }}
                className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-gold/15 blur-3xl"
            />
            <div className="pointer-events-none absolute bottom-0 left-[-15%] h-[420px] w-[420px] rounded-full bg-forest/10 blur-3xl" />

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 pb-24 pt-14 md:px-10 lg:grid-cols-12 lg:gap-8 lg:pt-20">
                <div className="flex flex-col justify-center lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-burgundy"
                    >
                        <span className="h-px w-10 bg-gold" />
                        {t.hero.eyebrow}
                    </motion.p>

                    <h1 className="font-serif text-5xl font-light leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
                        {t.hero.lines.map((line) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.italic ? "italic text-burgundy" : ""}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 1,
                                        delay: 0.25 + t.hero.lines.indexOf(line) * 0.14,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg"
                    >
                        {t.hero.introA}
                        <span className="font-medium text-ink">{t.hero.introName}</span>
                        {t.hero.introB}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToSection("#contacto")}
                            className="group flex items-center gap-2 rounded-full bg-burgundy px-8 py-4 text-sm tracking-wide text-ivory transition-[background-color,transform] duration-300 hover:bg-forest active:scale-[0.98]"
                        >
                            {t.hero.primary}
                            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                        </button>
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToSection("#iul")}
                            className="rounded-full border border-ink/20 px-8 py-4 text-sm tracking-wide text-ink transition-[border-color,color,transform] duration-300 hover:border-gold hover:text-burgundy active:scale-[0.98]"
                        >
                            {t.hero.secondary}
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.25em] text-ink/50"
                    >
                        {t.hero.trust.map((item, i) => (
                            <span key={item} className="flex items-center gap-8">
                                {i > 0 && <span className="text-gold">·</span>}
                                {item}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto max-w-sm lg:max-w-none"
                    >
                        <div className="absolute -inset-4 rounded-t-full border border-gold/40" />
                        <div className="absolute -inset-4 rounded-t-full bg-gold/10 blur-xl" />
                        <div className="relative overflow-hidden rounded-t-full border border-gold/50 bg-gradient-to-b from-tan/50 via-ivory to-tan/70 shadow-2xl shadow-burgundy/20">
                            <motion.img
                                style={{ y: portraitY }}
                                src={IMAGES.fabiana}
                                alt={t.hero.portraitAlt}
                                data-testid="hero-portrait"
                                className="h-[420px] w-full scale-110 object-cover object-top sm:h-[520px]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-burgundy/30 via-transparent to-transparent" />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.3 }}
                            className="absolute -bottom-6 left-1/2 flex w-[85%] -translate-x-1/2 items-center gap-3 border border-gold/30 bg-forest/95 px-5 py-4 text-ivory shadow-xl backdrop-blur-md"
                        >
                            <ShieldCheck className="h-6 w-6 shrink-0 text-gold" />
                            <div>
                                <p className="font-serif text-base leading-tight">
                                    {t.hero.badgeTitle}
                                </p>
                                <p className="text-xs text-ivory/70">{t.hero.badgeSub}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
